
import type { HotkeyConfig, KeyModifier } from "../types";

class LRUCache<K, V> {
  private maxSize: number;
  private map: Map<K, V>;
  constructor(maxSize: number = 100) {
    this.maxSize = maxSize;
    this.map = new Map();
  }
  get(key: K): V | undefined {
    if (!this.map.has(key)) return undefined;
    const value = this.map.get(key)!;
    this.map.delete(key);
    this.map.set(key, value);
    return value;
  }
  set(key: K, value: V) {
    if (this.map.has(key)) this.map.delete(key);
    this.map.set(key, value);
    if (this.map.size > this.maxSize) {
      const firstKey = this.map.keys().next().value;
      if (firstKey !== undefined) {
        this.map.delete(firstKey as K);
      }
    }
  }

  clear(): void {
    this.map.clear();
  }

  get size(): number {
    return this.map.size;
  }
}

const suggestKeyCache = new LRUCache<string, KeySuggestion>(100);
const validateShortcutCache = new LRUCache<string, { isValid: boolean; isSafe: boolean; warnings: string[]; suggestions: string[] }>(100);
const conflictCheckCache = new LRUCache<string, { hasConflict: boolean; conflictWith?: string }>(100);
const alternativesCache = new LRUCache<string, Array<{ key: string; modifiers: KeyModifier[]; reasoning: string }>>(100);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function createCacheKey(action: string, existingShortcuts: HotkeyConfig[], options: any): string {
  const existingSignature = existingShortcuts
    .map((s) => `${s.key}:${(s.modifiers || []).join(",")}:${s.scope || "global"}`)
    .sort()
    .join("|");

  return JSON.stringify({
    action,
    existing: existingSignature,
    options,
  });
}

export const STANDARD_SHORTCUTS = new Set([
  "ctrl+c",
  "ctrl+v",
  "ctrl+x",
  "meta+c",
  "meta+v",
  "meta+x",

  "ctrl+z",
  "ctrl+y",
  "ctrl+a",
  "meta+z",
  "meta+y",
  "meta+a",

  "ctrl+f",
  "ctrl+t",
  "ctrl+w",
  "ctrl+n",
  "ctrl+r",
  "ctrl+l",
  "ctrl+d",
  "ctrl+h",
  "ctrl+p",
  "ctrl+shift+t",
  "ctrl+shift+n",
  "meta+t",
  "meta+w",
  "meta+n",
  "meta+r",
  "meta+l",
]);

export const SYSTEM_SHORTCUTS = new Set(["ctrl+alt+del", "meta+q", "alt+f4", "meta+tab", "alt+tab", "meta+space", "ctrl+esc"]);

export const SCREEN_READER_KEYS = new Set(["h", "t", "l", "f", "b", "i", "d", "r", "g", "1", "2", "3", "4", "5", "6"]);

export const MODIFIER_PRIORITY = {
  ctrl: 1,
  meta: 1,
  alt: 2,
  "ctrl+shift": 3,
  "meta+shift": 3,
  "ctrl+alt": 4,
  "alt+shift": 5,
} as const;

export interface KeySuggestion {
  key: string;
  modifiers: KeyModifier[];
  reasoning: string[];
  confidence: number;
  conflicts: string[];
  alternatives: Array<{ key: string; modifiers: KeyModifier[]; reasoning: string }>;
}

export function suggestKey(
  action: string,
  existingShortcuts: HotkeyConfig[] = [],
  options: {
    allowSingleKey?: boolean;
    scope?: string;
    preferAlt?: boolean;
  } = {},
): KeySuggestion {
  const cacheKey = createCacheKey(action, existingShortcuts, options);
  const cached = suggestKeyCache.get(cacheKey);
  if (cached) {
    return cached;
  }

  const reasoning: string[] = [];
  const conflicts: string[] = [];

  const firstLetter = action.trim()[0]?.toLowerCase();
  if (!firstLetter) {
    throw new Error("Action must not be empty");
  }

  reasoning.push(`Using first letter '${firstLetter}' from "${action}"`);

  const ctrlCombo = `ctrl+${firstLetter}`;
  const ctrlConflict = checkConflict(ctrlCombo, existingShortcuts);

  if (!ctrlConflict.hasConflict) {
    const result: KeySuggestion = {
      key: firstLetter,
      modifiers: ["ctrl"],
      confidence: 95,
      reasoning: [...reasoning, "Ctrl modifier makes it WCAG compliant", "Standard pattern for actions", "No conflicts detected"],
      conflicts: [],
      alternatives: generateAlternatives(firstLetter, existingShortcuts),
    };

    suggestKeyCache.set(cacheKey, result);
    return result;
  }

  conflicts.push(`Ctrl+${firstLetter.toUpperCase()} conflicts with: ${ctrlConflict.conflictWith}`);

  const altCombo = `alt+${firstLetter}`;
  const altConflict = checkConflict(altCombo, existingShortcuts);

  if (!altConflict.hasConflict || options.preferAlt) {
    const result: KeySuggestion = {
      key: firstLetter,
      modifiers: ["alt"],
      confidence: 85,
      reasoning: [
        ...reasoning,
        "Alt modifier makes it WCAG compliant",
        "Good for menu access",
        altConflict.hasConflict ? "⚠️ May conflict with system" : "No conflicts",
      ],
      conflicts: altConflict.hasConflict ? [`Alt+${firstLetter.toUpperCase()} may conflict with system menus`] : [],
      alternatives: generateAlternatives(firstLetter, existingShortcuts),
    };

    suggestKeyCache.set(cacheKey, result);
    return result;
  }

  const ctrlShiftCombo = `ctrl+shift+${firstLetter}`;
  const ctrlShiftConflict = checkConflict(ctrlShiftCombo, existingShortcuts);

  if (!ctrlShiftConflict.hasConflict) {
    const result: KeySuggestion = {
      key: firstLetter,
      modifiers: ["ctrl", "shift"],
      confidence: 75,
      reasoning: [...reasoning, "Ctrl+Shift makes it WCAG compliant", "Good for extended actions", "No conflicts detected"],
      conflicts: [],
      alternatives: generateAlternatives(firstLetter, existingShortcuts),
    };

    suggestKeyCache.set(cacheKey, result);
    return result;
  }

  conflicts.push(`Ctrl+Shift+${firstLetter.toUpperCase()} conflicts with: ${ctrlShiftConflict.conflictWith}`);

  if (options.allowSingleKey && options.scope) {
    const result: KeySuggestion = {
      key: firstLetter,
      modifiers: [],
      confidence: 60,
      reasoning: [
        ...reasoning,
        "⚠️ Single key without modifiers",
        `WCAG compliant because scoped to "${options.scope}"`,
        "Users can disable via preferences",
        "Best for power users",
      ],
      conflicts: ["Requires explicit scope"],
      alternatives: generateAlternatives(firstLetter, existingShortcuts),
    };

    suggestKeyCache.set(cacheKey, result);
    return result;
  }

  const secondLetter = extractSecondLetter(action);
  if (secondLetter && secondLetter !== firstLetter) {
    const result: KeySuggestion = {
      key: secondLetter,
      modifiers: ["ctrl"],
      confidence: 50,
      reasoning: [`First letter conflicted, using second letter '${secondLetter}'`, "Ctrl modifier makes it WCAG compliant"],
      conflicts,
      alternatives: generateAlternatives(secondLetter, existingShortcuts),
    };

    suggestKeyCache.set(cacheKey, result);
    return result;
  }

  const result: KeySuggestion = {
    key: firstLetter,
    modifiers: ["ctrl", "alt"],
    confidence: 30,
    reasoning: [...reasoning, "⚠️ No good shortcuts available", "Consider using a different key or removing this shortcut"],
    conflicts,
    alternatives: generateAlternatives(firstLetter, existingShortcuts),
  };

  suggestKeyCache.set(cacheKey, result);
  return result;
}

function checkConflict(combo: string, existing: HotkeyConfig[]): { hasConflict: boolean; conflictWith?: string } {
  const existingSignature = existing
    .map((s) => `${s.key}:${(s.modifiers || []).join(",")}`)
    .sort()
    .join("|");
  const cacheKey = `${combo}:${existingSignature}`;

  const cached = conflictCheckCache.get(cacheKey);
  if (cached) {
    return cached;
  }

  let result: { hasConflict: boolean; conflictWith?: string };

  if (STANDARD_SHORTCUTS.has(combo)) {
    result = { hasConflict: true, conflictWith: "standard shortcut" };
  } else if (SYSTEM_SHORTCUTS.has(combo)) {
    result = { hasConflict: true, conflictWith: "system shortcut" };
  } else {
    const [modifiers, key] = parseCombo(combo);
    let hasConflict = false;
    let conflictWith: string | undefined;

    for (const shortcut of existing) {
      const existingModifiers = shortcut.modifiers || [];
      if (shortcut.key === key && arraysEqual(existingModifiers, modifiers)) {
        hasConflict = true;
        conflictWith = shortcut.description;
        break;
      }
    }

    result = { hasConflict, conflictWith };
  }

  conflictCheckCache.set(cacheKey, result);
  return result;
}

function parseCombo(combo: string): [KeyModifier[], string] {
  const parts = combo.split("+");
  const key = parts[parts.length - 1];
  const modifiers = parts.slice(0, -1) as KeyModifier[];
  return [modifiers, key];
}

function arraysEqual<T>(a: T[], b: T[]): boolean {
  if (a.length !== b.length) return false;
  const sortedA = [...a].sort();
  const sortedB = [...b].sort();
  return sortedA.every((val, i) => val === sortedB[i]);
}

function extractSecondLetter(action: string): string | null {
  const consonants = action
    .toLowerCase()
    .replace(/[^a-z]/g, "")
    .split("");
  return consonants[1] || null;
}

function generateAlternatives(
  primaryKey: string,
  existing: HotkeyConfig[],
): Array<{ key: string; modifiers: KeyModifier[]; reasoning: string }> {
  const existingSignature = existing
    .map((s) => `${s.key}:${(s.modifiers || []).join(",")}`)
    .sort()
    .join("|");
  const cacheKey = `${primaryKey}:${existingSignature}`;

  const cached = alternativesCache.get(cacheKey);
  if (cached) {
    return cached;
  }

  const alternatives: Array<{ key: string; modifiers: KeyModifier[]; reasoning: string }> = [];

  const modifierCombos: Array<{ modifiers: KeyModifier[]; reasoning: string }> = [
    { modifiers: ["ctrl", "shift"], reasoning: "Extended action variant" },
    { modifiers: ["alt"], reasoning: "Menu-style access" },
    { modifiers: ["ctrl", "alt"], reasoning: "Advanced action" },
  ];

  for (const { modifiers, reasoning } of modifierCombos) {
    const combo = `${modifiers.join("+")}+${primaryKey}`;
    const conflict = checkConflict(combo, existing);
    if (!conflict.hasConflict) {
      alternatives.push({
        key: primaryKey,
        modifiers,
        reasoning,
      });
    }
  }

  const relatedKeys = getRelatedKeys(primaryKey);
  for (const relatedKey of relatedKeys) {
    const combo = `ctrl+${relatedKey}`;
    const conflict = checkConflict(combo, existing);
    if (!conflict.hasConflict) {
      alternatives.push({
        key: relatedKey,
        modifiers: ["ctrl"],
        reasoning: `Related to '${primaryKey}'`,
      });
    }
  }

  const result = alternatives.slice(0, 3);

  alternativesCache.set(cacheKey, result);

  return result;
}

//* QWERTY keyboard proximity map for suggesting alternative keys
const RELATED_KEYS: Record<string, string[]> = {
  a: ["s", "q", "w", "z"],
  s: ["a", "d", "w", "e", "x"],
  d: ["s", "f", "e", "r", "c"],
  f: ["d", "g", "r", "t", "v"],
  g: ["f", "h", "t", "y", "b"],
  h: ["g", "j", "y", "u", "n"],
  j: ["h", "k", "u", "i", "m"],
  k: ["j", "l", "i", "o"],
  l: ["k", "o", "p"],
  e: ["w", "r", "d", "s"],
  r: ["e", "t", "f", "d"],
  t: ["r", "y", "g", "f"],
  y: ["t", "u", "h", "g"],
  u: ["y", "i", "j", "h"],
  i: ["u", "o", "k", "j"],
  o: ["i", "p", "l", "k"],
  p: ["o", "l"],
  q: ["w", "a"],
  w: ["q", "e", "s", "a"],
  z: ["a", "s", "x"],
  x: ["z", "s", "d", "c"],
  c: ["x", "d", "f", "v"],
  v: ["c", "f", "g", "b"],
  b: ["v", "g", "h", "n"],
  n: ["b", "h", "j", "m"],
  m: ["n", "j", "k"],
};

function getRelatedKeys(key: string): string[] {
  return RELATED_KEYS[key.toLowerCase()] || [];
}

export function validateShortcut(config: HotkeyConfig): {
  isValid: boolean;
  isSafe: boolean;
  warnings: string[];
  suggestions: string[];
} {
  const cacheKey = JSON.stringify({
    key: config.key,
    modifiers: config.modifiers || [],
    scope: config.scope,
  });

  const cached = validateShortcutCache.get(cacheKey);
  if (cached) {
    return cached;
  }

  const warnings: string[] = [];
  const suggestions: string[] = [];
  let isValid = true;
  let isSafe = true;

  const { key, modifiers = [], scope } = config;
  const combo = modifiers.length > 0 ? `${modifiers.join("+")}+${key}` : key;

  if (STANDARD_SHORTCUTS.has(combo)) {
    warnings.push(`⚠️ Overrides standard shortcut: ${combo.toUpperCase()}`);
    suggestions.push("Use a different key or add more modifiers");
    isSafe = false;
  }

  if (SYSTEM_SHORTCUTS.has(combo)) {
    warnings.push(`⚠️ Conflicts with system shortcut: ${combo.toUpperCase()}`);
    suggestions.push("Choose a different key combination");
    isSafe = false;
  }

  if (modifiers.length === 0 && key.length === 1 && !scope) {
    warnings.push("❌ WCAG 2.1.4 violation: Single key without modifiers or scope");
    suggestions.push("Add modifiers (ctrl, alt, shift) OR scope to a focused element");
    isValid = false;
  }

  if (modifiers.length === 0 && SCREEN_READER_KEYS.has(key.toLowerCase())) {
    warnings.push(`⚠️ May conflict with screen reader navigation: ${key.toUpperCase()}`);
    if (scope) {
      suggestions.push("OK because scoped, but test with screen readers");
    } else {
      suggestions.push("Add modifiers or scope to focused element");
      isSafe = false;
    }
  }

  const result = {
    isValid,
    isSafe,
    warnings,
    suggestions,
  };

  validateShortcutCache.set(cacheKey, result);

  return result;
}

export function suggestShortcuts(
  actions: string[],
  options: {
    allowSingleKey?: boolean;
    scope?: string;
  } = {},
): Map<string, KeySuggestion> {
  const suggestions = new Map<string, KeySuggestion>();
  const existing: HotkeyConfig[] = [];

  for (const action of actions) {
    const suggestion = suggestKey(action, existing, options);
    suggestions.set(action, suggestion);

    existing.push({
      key: suggestion.key,
      modifiers: suggestion.modifiers,
      description: action,
      handler: () => {}, //* Placeholder handler - will be overridden by caller
    });
  }

  return suggestions;
}

//* QWERTY keyboard proximity map for suggesting alternative keys based on physical layout
export function recommendPattern(actionType: "navigation" | "action" | "menu" | "editing"): {
  pattern: string;
  examples: HotkeyConfig[];
  description: string;
} {
  const patterns = {
    navigation: {
      pattern: "Scoped single keys or arrow keys",
      description: "Best for list navigation, only active when component has focus",
      examples: [
        { key: "j", scope: "list", description: "Next item", handler: () => {} },
        { key: "k", scope: "list", description: "Previous item", handler: () => {} },
        { key: "ArrowDown", scope: "list", description: "Next item", handler: () => {} },
        { key: "ArrowUp", scope: "list", description: "Previous item", handler: () => {} },
      ],
    },
    action: {
      pattern: "Ctrl/Cmd + mnemonic letter",
      description: "Standard for actions like Save, Open, Close",
      examples: [
        { key: "s", modifiers: ["ctrl"] as KeyModifier[], description: "Save", handler: () => {} },
        { key: "o", modifiers: ["ctrl"] as KeyModifier[], description: "Open", handler: () => {} },
        { key: "p", modifiers: ["ctrl"] as KeyModifier[], description: "Print", handler: () => {} },
      ],
    },
    menu: {
      pattern: "Alt + first letter",
      description: "Standard for menu access",
      examples: [
        { key: "f", modifiers: ["alt"] as KeyModifier[], description: "File menu", handler: () => {} },
        { key: "e", modifiers: ["alt"] as KeyModifier[], description: "Edit menu", handler: () => {} },
        { key: "v", modifiers: ["alt"] as KeyModifier[], description: "View menu", handler: () => {} },
      ],
    },
    editing: {
      pattern: "Ctrl/Cmd + letter or symbol",
      description: "Standard editing shortcuts",
      examples: [
        { key: "b", modifiers: ["ctrl"] as KeyModifier[], description: "Bold", handler: () => {} },
        { key: "i", modifiers: ["ctrl"] as KeyModifier[], description: "Italic", handler: () => {} },
        { key: "u", modifiers: ["ctrl"] as KeyModifier[], description: "Underline", handler: () => {} },
      ],
    },
  };

  return patterns[actionType] || patterns.action;
}

export function clearKeySelectorCaches(): void {
  suggestKeyCache.clear();
  validateShortcutCache.clear();
  conflictCheckCache.clear();
  alternativesCache.clear();
}

export function getKeySelectorCacheStats(): {
  suggestKey: { size: number };
  validateShortcut: { size: number };
  conflictCheck: { size: number };
  alternatives: { size: number };
  total: { size: number };
} {
  return {
    suggestKey: { size: suggestKeyCache.size },
    validateShortcut: { size: validateShortcutCache.size },
    conflictCheck: { size: conflictCheckCache.size },
    alternatives: { size: alternativesCache.size },
    total: {
      size: suggestKeyCache.size + validateShortcutCache.size + conflictCheckCache.size + alternativesCache.size,
    },
  };
}
